import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.category-dropdown')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`text-lg font-heading font-semibold whitespace-nowrap transition-colors ${
              scrolled
                ? 'text-foreground-950 hover:text-primary-500'
                : 'text-background-50 hover:text-background-200'
            }`}
          >
            AllChinaBuy<span className={scrolled ? 'text-primary-500' : 'text-primary-400'}>Spreadsheet</span>
          </Link>

          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/"
              className={`text-sm transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950'
                  : 'text-background-200 hover:text-background-50'
              }`}
            >
              Home
            </Link>
            <a
              href="#categories"
              className={`text-sm transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950'
                  : 'text-background-200 hover:text-background-50'
              }`}
            >
              Categories
            </a>
            <a
              href="#sellers"
              className={`text-sm transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950'
                  : 'text-background-200 hover:text-background-50'
              }`}
            >
              Sellers
            </a>
            <Link
              to="/allchinabuy-spreadsheet-guide"
              className={`text-sm transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950'
                  : 'text-background-200 hover:text-background-50'
              }`}
            >
              Shopping Guide
            </Link>
            <a
              href="#safety"
              className={`text-sm transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950'
                  : 'text-background-200 hover:text-background-50'
              }`}
            >
              Safety
            </a>
            <a
              href="#faq"
              className={`text-sm transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950'
                  : 'text-background-200 hover:text-background-50'
              }`}
            >
              FAQ
            </a>

            <div className="relative category-dropdown">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'border-background-300/60 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                    : 'border-background-50/30 text-background-200 hover:border-background-50/60 hover:text-background-50'
                }`}
              >
                <span>Browse Categories</span>
                {dropdownOpen ? (
                  <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-up-s-line text-xs"></i></span>
                ) : (
                  <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-down-s-line text-xs"></i></span>
                )}
              </button>

              {dropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-background-50 border border-background-200/70 rounded-lg shadow-lg max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer border-b border-background-200/40 last:border-0"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-medium bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              allchinabuy sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-4 py-2 text-sm font-medium bg-foreground-900 text-background-50 rounded-full hover:bg-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              buy it now
              <span className="w-4 h-4 flex items-center justify-center ml-1">
                <i className="ri-arrow-right-line text-xs"></i>
              </span>
            </a>

            <button
              className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer transition-colors ${
                scrolled ? 'text-foreground-700' : 'text-background-50'
              }`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <i className="ri-close-line text-xl"></i>
              ) : (
                <i className="ri-menu-line text-xl"></i>
              )}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 max-h-[70vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg">
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-home-line text-sm"></i>
              </span>
              Home
            </Link>
            <Link to="/allchinabuy-spreadsheet-guide" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg">
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-book-open-line text-sm"></i>
              </span>
              Shopping Guide
            </Link>
            <div className="border-t border-background-200/70 my-2"></div>
            <p className="px-3 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Page Sections</p>
            <a href="#categories" onClick={() => setMobileMenuOpen(false)} className="px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 rounded-lg">Categories</a>
            <a href="#sellers" onClick={() => setMobileMenuOpen(false)} className="px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 rounded-lg">Sellers</a>
            <a href="#safety" onClick={() => setMobileMenuOpen(false)} className="px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 rounded-lg">Safety</a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 rounded-lg">FAQ</a>
            <div className="border-t border-background-200/70 my-2"></div>
            <p className="px-3 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Browse 14 Categories</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2 text-xs text-foreground-700 hover:bg-background-100 rounded-lg"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-primary-500 flex-shrink-0">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}